import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthContext } from 'contexts/auth.context';
import { useSendOtp } from 'lib/hooks/auth/auth.mutations';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import InputMask from 'react-text-mask';
import { countryCodeSchema, phoneNumberSchema } from 'schemas/common.schemas';
import { z } from 'zod';
import '../styles.scss';

const phoneNumberMask = [
  '(',
  /[1-9]/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

const sendOtpFormSchema = z.object({
  userId: z.number(),
  countryCode: countryCodeSchema,
  phone: phoneNumberSchema,
});

type SendOtpFormSchema = z.infer<typeof sendOtpFormSchema>;

type SendOtpProps = {
  onSent: (mobileLastFour: string) => void | undefined;
};

function SendOtp({ onSent }: SendOtpProps) {
  const sendOtpMutation = useSendOtp();

  const { user } = useAuthContext();

  const {
    formState: { errors, isSubmitting },
    ...form
  } = useForm<SendOtpFormSchema>({
    defaultValues: {
      userId: user?.id ?? 0,
      phone: user?.phone.replace(/[^\d]/g, '') ?? '',
      countryCode: user?.countryCode ?? '1',
    },
    resolver: zodResolver(sendOtpFormSchema),
  });

  const handleSendOtp = async (values: SendOtpFormSchema) => {
    try {
      const formattedPhoneNumber = values.phone.replace(/\D/g, '');
      const payload = {
        userId: values.userId,
      };
      const res = await sendOtpMutation.mutateAsync(payload);
      if (res?.success) {
        const mobileLastFour = formattedPhoneNumber.slice(-4);
        onSent?.(mobileLastFour);
      }
    } catch (error: any) {
      const message = error?.data?.message || 'Something went wrong';
      form.setError('phone', {
        message,
      });
    }
  };

  return (
    <>
      <div id="enterPhoneScreen" className="enterPhoneScreen">
        <h2 className="title">Let's secure your account</h2>
        <p className="subtitle">
          We are implementing two-factor best-practices to enhance the security
          of your Ultra account. Please enter your mobile phone number below to
          receive a login code via SMS.
        </p>
        <div className="authCodeWarp">
          <form onSubmit={form.handleSubmit(handleSendOtp)} noValidate>
            <div className="row">
              <div className="col-3">
                <div className="form-group material-textfield">
                  <div className="phone-input-styled">
                    <Controller
                      name="countryCode"
                      control={form.control}
                      render={({ field }) => (
                        <PhoneInput
                          {...field}
                          inputProps={{
                            readOnly: true,
                            required: true,
                            autoFocus: true,
                            className:
                              'form-control form-control-lg material-textfield-input',
                          }}
                          country={'us'}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="col-9">
                <div className="form-group material-textfield">
                  <Controller
                    name="phone"
                    control={form.control}
                    render={({ field }) => (
                      <InputMask
                        {...field}
                        ref={null}
                        guide={false}
                        type="text"
                        id="phone"
                        keepCharPositions={false}
                        mask={phoneNumberMask}
                        className="form-control form-control-lg material-textfield-input"
                        required
                      />
                    )}
                  />
                  <label className="material-textfield-label" htmlFor="phone">
                    Phone Number
                  </label>
                  <div className="invalid-feedback d-block">
                    {errors.phone?.message || errors.countryCode?.message}
                  </div>
                </div>
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-dark btn-lg w-100"
            >
              {isSubmitting ? 'Please wait ...' : 'Send me a code'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

export default SendOtp;
